import { Link, useLocation } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useHashScroll } from "../lib/hooks";
import { PageHero, CTABanner } from "../components/Shared";
import { SERVICES, FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const ServiceImage = ({ image, title, reversed }) => (
  <div className={reversed ? "lg:order-2" : ""}>
    <div
      className="overflow-hidden"
      style={{
        boxShadow: reversed
          ? `-12px 12px 0px ${GOLD}, 0 10px 40px rgba(27,58,107,0.15)`
          : `12px 12px 0px ${GOLD}, 0 10px 40px rgba(27,58,107,0.15)`,
      }}
    >
      <img src={image} alt={`${title} — professional cleaning service in Naples, FL by Cindyrella Cleaning LLC`} loading="lazy" className="w-full h-64 md:h-80 object-cover" />
    </div>
  </div>
);

const IncludedList = ({ items }) => (
  <div className="space-y-3 mb-8">
    {items.map((item) => (
      <div key={item} className="flex items-start gap-3">
        <CheckCircle size={17} style={{ color: GOLD, flexShrink: 0, marginTop: "2px" }} />
        <span className="text-sm" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>
          {item}
        </span>
      </div>
    ))}
  </div>
);

const ServiceContent = ({ id, Icon, title, longDescription, included, link, reversed }) => (
  <div className={reversed ? "lg:order-1" : ""}>
    <div className="w-12 h-12 flex items-center justify-center mb-5" style={{ background: "rgba(212,168,67,0.15)", borderRadius: "2px" }}>
      <Icon size={24} style={{ color: GOLD }} />
    </div>
    <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      {title}
    </h2>
    <p className="text-base leading-relaxed mb-6" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      {longDescription}
    </p>
    <IncludedList items={included} />
    <Link
      to="/contact"
      data-testid={`service-${id}-quote-btn`}
      className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      Get a Quote for {title}
      <ArrowRight size={16} />
    </Link>
    {link && (
      <Link
        to={link}
        data-testid={`service-${id}-learn-more`}
        className="inline-flex items-center gap-1.5 ml-4 text-sm font-bold hover:gap-2.5 transition-all"
        style={{ color: GOLD, fontFamily: FONT_BODY }}
      >
        Learn More <ArrowRight size={14} />
      </Link>
    )}
  </div>
);

const ServiceDetail = ({ service, idx }) => {
  const reversed = idx % 2 === 1;
  return (
    <section
      id={service.id}
      data-testid={`service-detail-${service.id}`}
      className="py-16 md:py-24"
      style={{ background: idx % 2 === 0 ? "#FFFFFF" : "#F9FAFB", scrollMarginTop: "90px" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <ServiceImage image={service.image} title={service.title} reversed={reversed} />
          <ServiceContent {...service} reversed={reversed} />
        </div>
      </div>
    </section>
  );
};

export default function ServicesPage() {
  const { hash } = useLocation();
  useHashScroll(hash);

  return (
    <>
      <PageHero
        testid="services-page-hero"
        badge="Our Services"
        title={<>Cleaning Services Built for <span style={{ color: GOLD }}>Naples</span></>}
        subtitle="From luxury residences to commercial spaces — explore each service in detail and find the perfect fit for your space."
      />
      {SERVICES.map((service, idx) => (
        <ServiceDetail key={service.id} service={service} idx={idx} />
      ))}
      <section data-testid="services-related-note" className="py-10 bg-white">
        <p className="max-w-7xl mx-auto px-4 md:px-8 text-center text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
          Wrapping up a construction or renovation project? We also provide{" "}
          <Link to="/services/post-construction-cleaning-naples-fl" data-testid="services-post-construction-link" className="font-bold transition-colors hover:text-[#b8922a]" style={{ color: GOLD }}>
            Post-Construction Cleaning
          </Link>{" "}
          across Naples and Collier County.
        </p>
      </section>
      <CTABanner testid="services-cta-banner" />
    </>
  );
}
